import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setMobileOpen(false);
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/products', label: t('nav.products') },
    { path: '/about', label: t('nav.company') },
    { path: '/downloads', label: t('nav.downloads') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/news', label: t('nav.news') },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="flex flex-col leading-none">
              <span className={`font-heading text-2xl md:text-3xl font-bold tracking-tight transition-colors duration-300 ${
                scrolled ? 'text-foreground-900' : 'text-white'
              }`}>
                BOCK
              </span>
              <span className={`text-[10px] md:text-xs font-body tracking-[0.2em] uppercase transition-colors duration-300 ${
                scrolled ? 'text-foreground-600' : 'text-white/80'
              }`}>
                Maschinenfabrik
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-300 whitespace-nowrap ${
                  isActive(link.path)
                    ? scrolled
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-white bg-white/15'
                    : scrolled
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Toggle + Mobile Menu */}
          <div className="flex items-center gap-3">
            <div className={`hidden sm:flex items-center rounded-full border transition-colors duration-300 overflow-hidden ${
              scrolled ? 'border-background-200' : 'border-white/30'
            }`}>
              <button
                onClick={() => changeLanguage('de')}
                className={`px-3 py-1 text-xs font-medium transition-all duration-300 ${
                  i18n.language === 'de'
                    ? scrolled ? 'bg-primary-500 text-white' : 'bg-white/20 text-white'
                    : scrolled ? 'text-foreground-600 hover:text-primary-600' : 'text-white/70 hover:text-white'
                }`}
              >
                DE
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1 text-xs font-medium transition-all duration-300 ${
                  i18n.language === 'en'
                    ? scrolled ? 'bg-primary-500 text-white' : 'bg-white/20 text-white'
                    : scrolled ? 'text-foreground-600 hover:text-primary-600' : 'text-white/70 hover:text-white'
                }`}
              >
                EN
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded-md transition-colors duration-300 ${
                scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label={t('nav.menu')}
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50/98 backdrop-blur-md border-t border-background-200 px-4 py-4 shadow-lg">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`block px-4 py-3 text-sm font-medium rounded-md transition-colors mb-1 ${
                isActive(link.path)
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-background-200">
            <span className="text-xs text-foreground-500">{t('nav.language')}:</span>
            <button
              onClick={() => changeLanguage('de')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                i18n.language === 'de' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              DE
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                i18n.language === 'en' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}